import { useCallback, useState, useRef, useEffect } from "react";
import { getActiveModelName, getCurrentMaterialName, getMatParameterFromModel, processReceivedMaterial, setAllModelParameters, setSurfaceTreatmentParameters,
    writeRecentMaterialToCache, getCurrentMatListFromCache, getCurrentMaterialDensity, getMaterialDataFromCacheByKey, deleteCacheFile, deleteCacheEntryByKey, removeAssignedMaterial, clearAllPluginParameters } from "../services/creoClient";
import "./PluginPage.css";
import PluginHeader from "../components/PluginHeader";
import AssignmentContextTable from "../components/AssignmentContextTable";
import RecentlyUsedTable from "../components/RecentlyUsedTable";
import DebugConsole, { DebugConsoleHandle } from "../components/DebugConsole";
import type { AssignmentContextRow, RecentlyUsedRow, CreoMatParameterItem, WizardPayload, WizardAssignmentItem, AlternateMaterialItem } from "../types/PluginTypes";
import { sendMessageToWizard, isAllowedOrigin } from "../services/wizardMessaging";
import { createLogger } from "../services/logger";
import { convertDensityToKgPerDm3 } from "../services/densityConversion";

const WIZZARD_ORIGINS = ["https://prosteptest.totalmateria.com", "https://abbprostep.totalmateria.com"];
const CONTEXT_REFRESH_INTERVAL_MS = 1000;
const CREO_SCRIPT_TYPE = "text/creojs";
const CREO_RUNTIME_SRC = "/creojs.js";
const CREO_CUSTOM_SCRIPT_SRCS = [
    "/browser.creojs",
    "/creo/logger.creojs",
    "/creo/cache.creojs",
    "/creo/materialHandler.creojs",
    "/creo/setup.creojs",
];

const logger = createLogger("PluginPage");
const MATERIAL_NAME_PARAMETER_NAMES = ["MATERIAL_NAME", "MLM_MATERIAL_NAME"] as const;

function getParameterByPreferredName(
    parameters: unknown[],
    names: readonly string[],
): Record<string, unknown> | undefined {
    for (const wantedName of names) {
        const match = parameters.find((parameter) => {
            if (!parameter || typeof parameter !== "object") {
                return false;
            }

            const nameValue = "Name" in parameter
                ? (parameter as { Name?: unknown }).Name
                : (parameter as { name?: unknown }).name;

            return typeof nameValue === "string" && nameValue === wantedName;
        });

        if (match && typeof match === "object") {
            return match as Record<string, unknown>;
        }
    }

    return undefined;
}

function getDensityFromParameters(parameters: unknown): string {
    if (!Array.isArray(parameters)) {
        return "";
    }

    const densityParameter = parameters.find((parameter) => {
        if (!parameter || typeof parameter !== "object") {
            return false;
        }

        const nameValue = "Name" in parameter
            ? (parameter as { Name?: unknown }).Name
            : (parameter as { name?: unknown }).name;

        return typeof nameValue === "string" && nameValue.includes("PTC_MASS_DENSITY");
    });

    if (!densityParameter || typeof densityParameter !== "object") {
        return "";
    }

    const defaultValue = "Default" in densityParameter
        ? (densityParameter as { Default?: unknown }).Default
        : (densityParameter as { default?: unknown }).default;

    if (!defaultValue || typeof defaultValue !== "object") {
        return "";
    }

    const rawValue = "Value" in defaultValue
        ? (defaultValue as { Value?: unknown }).Value
        : (defaultValue as { value?: unknown }).value;
    const rawUnit = "Unit" in defaultValue
        ? (defaultValue as { Unit?: unknown }).Unit
        : (defaultValue as { unit?: unknown }).unit;

    const value = typeof rawValue === "string" || typeof rawValue === "number" ? String(rawValue) : "";
    const unit = typeof rawUnit === "string" ? rawUnit : "";

    return `${value} ${unit}`.trim();
}

function getBmNameFromParameters(parameters: unknown): string {
    if (!Array.isArray(parameters)) {
        return "";
    }

    const materialNameParameter = getParameterByPreferredName(parameters, MATERIAL_NAME_PARAMETER_NAMES);
    if (!materialNameParameter) {
        return "";
    }

    const defaultValue = "Default" in materialNameParameter
        ? (materialNameParameter as { Default?: unknown }).Default
        : (materialNameParameter as { default?: unknown }).default;

    if (typeof defaultValue === "string" || typeof defaultValue === "number") {
        return String(defaultValue).trim();
    }

    if (!defaultValue || typeof defaultValue !== "object") {
        return "";
    }

    const rawValue = "Value" in defaultValue
        ? (defaultValue as { Value?: unknown }).Value
        : (defaultValue as { value?: unknown }).value;

    if (typeof rawValue === "string" || typeof rawValue === "number") {
        return String(rawValue).trim();
    }

    return "";
}

function ensureCreoCustomScripts() {
    for (const src of CREO_CUSTOM_SCRIPT_SRCS) {
        const existing = document.querySelector(`script[type=\"${CREO_SCRIPT_TYPE}\"][src=\"${src}\"]`);
        if (existing) {
            continue;
        }

        const script = document.createElement("script");
        script.type = CREO_SCRIPT_TYPE;
        script.src = src;
        document.head.appendChild(script);
    }
}

function initializeCreoRuntimeOnce() {
    if (window.__tmCreoRuntimeInitialized) {
        return;
    }

    if (!window.CreoJS) {
        return;
    }

    window.__tmCreoRuntimeInitialized = true;
    window.CreoJS.$INITIALIZE();
}

function ensureCreoRuntimeScript(): Promise<void> {
    if (window.CreoJS) {
        initializeCreoRuntimeOnce();
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src=\"${CREO_RUNTIME_SRC}\"]`) as HTMLScriptElement | null;
        if (existingScript) {
            existingScript.addEventListener("load", () => {
                initializeCreoRuntimeOnce();
                resolve();
            }, { once: true });
            existingScript.addEventListener("error", () => reject(new Error("Failed to load CreoJS runtime.")), { once: true });
            return;
        }

        const runtimeScript = document.createElement("script");
        runtimeScript.src = CREO_RUNTIME_SRC;
        runtimeScript.addEventListener("load", () => {
            initializeCreoRuntimeOnce();
            resolve();
        }, { once: true });
        runtimeScript.addEventListener("error", () => reject(new Error("Failed to load CreoJS runtime.")), { once: true });
        document.head.appendChild(runtimeScript);
    });
}

function resolveWizardPayloadFromMessage(message: Record<string, unknown>): WizardPayload {
    if ("material" in message && message.material && typeof message.material === "object") {
        return message.material as WizardPayload;
    }

    return message as WizardPayload;
}

function getAlternatesForPart(payload: WizardPayload, partNumber: string): AlternateMaterialItem[] {
    if (!Array.isArray(payload.alternates) || payload.alternates.length === 0) {
        return [];
    }

    return payload.alternates.filter((alternate) => String(alternate?.partNr ?? "") === partNumber);
}

function colonSeparatedToMultiline(value: unknown): string {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
        return "";
    }

    return normalized
        .split(":")
        .map((entry) => entry.trim())
        .join("\n");
}

function splitMultilineValues(value: string | undefined): string[] {
    return String(value ?? "")
        .split(/\r?\n/)
        .map((entry) => entry.trim());
}

function removeAlternateDecoratorsFromName(rawName: string, abbId: string, conditionLabel: string): string {
    let normalizedName = String(rawName ?? "").trim();
    const normalizedAbbId = String(abbId ?? "").trim();
    const normalizedConditionLabel = String(conditionLabel ?? "").trim();

    if (normalizedAbbId) {
        const prefix = `${normalizedAbbId}_`;
        if (normalizedName.startsWith(prefix)) {
            normalizedName = normalizedName.slice(prefix.length);
        }
    }

    if (normalizedConditionLabel) {
        const suffix = `_${normalizedConditionLabel}`;
        if (normalizedName.endsWith(suffix)) {
            normalizedName = normalizedName.slice(0, normalizedName.length - suffix.length);
        }
    }

    return normalizedName.trim();
}

function buildWizardAlternatesFromRow(
    row: AssignmentContextRow,
    conditionValueKinds?: Map<string, "conditionLabel" | "dimensionValue">,
): AlternateMaterialItem[] {
    const altIds = splitMultilineValues(row.altMatId);
    const altNames = splitMultilineValues(row.altMatName);
    const altAbbIds = splitMultilineValues(row.altMatAbbId);
    const altConditionLabels = splitMultilineValues(row.altMatConditionLabel);
    const altConditions = splitMultilineValues(row.altConditionId);

    const alternateCount = Math.max(
        altIds.length,
        altNames.length,
        altAbbIds.length,
        altConditionLabels.length,
        altConditions.length,
    );
    const alternates: AlternateMaterialItem[] = [];

    for (let index = 0; index < alternateCount; index += 1) {
        const bmId = (altIds[index] ?? "").trim();
        if (!bmId) {
            continue;
        }

        const plainBmName = removeAlternateDecoratorsFromName(
            altNames[index] ?? "",
            altAbbIds[index] ?? "",
            altConditionLabels[index] ?? "",
        );
        const abbId = (altAbbIds[index] ?? "").trim();
        const conditionCode = (altConditions[index] ?? "").trim();
        const conditionLabel = (altConditionLabels[index] ?? "").trim();

        const conditionKind = conditionValueKinds?.get(`${row.modelName}:${index}`) ?? "conditionLabel";

        alternates.push({
            bmId,
            ...(plainBmName ? { bmName: plainBmName } : {}),
            ...(abbId ? { abbId } : {}),
            ...((conditionCode || conditionLabel)
                ? {
                    condition: {
                        ...(conditionCode ? { code: conditionCode } : {}),
                        ...(conditionLabel
                            ? conditionKind === "dimensionValue"
                                ? { dimensionValue: conditionLabel }
                                : { conditionLabel }
                            : {}),
                    },
                }
                : {}),
        });
    }

    return alternates;
}

declare global {
  interface Window {
    debug?: (msg: string) => Promise<void>;
        __tmCreoRuntimeInitialized?: boolean;
  }

  var debug: ((msg: string) => Promise<void>) | undefined;
}

export default function PluginPage() {
    const [contextRows, setContextRows] = useState<AssignmentContextRow[]>([]);
    const [recentlyUsedRows, setRecentlyUsedRows] = useState<RecentlyUsedRow[]>([]);
    const [selectedRow, setSelectedRow] = useState<RecentlyUsedRow | null>(null);
    const [isCreoRuntimeReady, setIsCreoRuntimeReady] = useState(false);
    const contextSignatureRef = useRef<string>("");
    const contextRefreshInFlightRef = useRef(false);
    const isCoverageEditingRef = useRef(false);
    const debugRef = useRef<DebugConsoleHandle>(null);
    const conditionValueKindsRef = useRef<Map<string, "conditionLabel" | "dimensionValue">>(new Map());
    const showDebug = isDebugEnabled();
    const wizardWinRef = useRef<Window | null>(null);

    function parseThicknessFromContext(thickness: string): { value: string; unit: string } {
        const normalized = String(thickness ?? "").trim();
        if (!normalized) {
            return { value: "0", unit: "micron" };
        }

        const [value = "0", ...unitParts] = normalized.split(" ");
        const unit = unitParts.join(" ").trim() || "micron";

        return { value, unit };
    }

    const handleCoverageChange = useCallback(async (row: AssignmentContextRow, decimalCoverage: string) => {
        setContextRows((prevRows) => {
            const nextRows = prevRows.map((prevRow) =>
                prevRow.modelName === row.modelName
                    ? { ...prevRow, stCoverage: decimalCoverage }
                    : prevRow
            );

            contextSignatureRef.current = JSON.stringify(nextRows);
            return nextRows;
        });

        const thickness = parseThicknessFromContext(row.stThickness);

        const payload = {
            rowKey: "",
            name: row.bmName ?? "",
            bmId: row.bmId ?? "",
            stMaterialId: row.stMaterialId ?? "",
            bmName: row.bmName ?? "",
            bmDensity: { value: "", unit: "kg/dm3" },
            stName: row.stName ?? "",
            stDensity: { value: "", unit: "kg/dm3" },
            stCoverage: { value: decimalCoverage, unit: "decimal" },
            stThickness: thickness,
            alternates: buildWizardAlternatesFromRow(row),
            parameters: {},
            rawMTLString: "",
            partNumber: row.modelName,
        } as WizardPayload & { partNumber: string };

        await setSurfaceTreatmentParameters(payload);
    }, []);

    const getAssignmentContextPayload = useCallback(() => {
        if (contextRows.length === 0) {
            return [];
        }

        return contextRows.map(row => ({
            partNumber: row.modelName,
            bmId: row.bmId,
            stMaterialId: String(row.stMaterialId),
            bmName: row.bmName,
            stName: row.stName,
            density: row.bmDensity,
            coverage: row.stCoverage,
            thickness: row.stThickness.split(" ")[0],
            isAssembly: row.isAssembly,
            alternates: buildWizardAlternatesFromRow(row, conditionValueKindsRef.current),
        }));
    }, [contextRows]);

    const sendToWizard = useCallback(() => {
        const payload = getAssignmentContextPayload();

        // For debugging without Creo connection
        // const payload =
        // [
        //   {
        //     "partNumber": "PRT0001",
        //     "bmId": "1SAA100268",
        //     "stMaterialId": "",
        //     "bmName": "PA46_30GF_V-0",
        //     "stName": "",
        //     "density": "1.68 kg/dm³",
        //     "coverage": 0,
        //     "thickness": "0",
        //     "isAssembly": false,
        //     "alternates": [
        //       {
        //         "bmId": "1SAA100264",
        //         "bmName": "PA66_25GF_V-0",
        //         "abbId": "ABC123",
        //         "condition": {
        //             "code": "8004",
        //             "conditionLabel": "test"
        //         }
        //       },
        //       {
        //         "bmId": "1SAA100311",
        //         "bmName": "BMC_20GF_V-0_HT",
        //         "conditionCode": "8002"
        //       }
        //     ]
        //   }
        // ]
        //debugRef.current?.log(JSON.stringify(payload, null, 2));

        if (payload.length === 0) {
            logger.warning("No assignment context available.");
            return;
        }

        sendMessageToWizard(
            wizardWinRef.current,
            payload,
            WIZZARD_ORIGINS[1]
        );
    }, [getAssignmentContextPayload]);

    const onMessageFromWizard = useCallback(
        (ev: MessageEvent) => {
            if (!isAllowedOrigin(ev.origin, WIZZARD_ORIGINS)) {
                logger.error(`Unknown origin '${ev.origin}'.`);
                return;
            }

            const msg = ev.data;
            //debugRef.current?.log(JSON.stringify(msg, null, 2));

            if (typeof msg === "string") {
                if (msg === "ready") {
                    sendToWizard();
                }
            } else if (typeof msg === "object" && msg !== null) {
                processWizardPayload(resolveWizardPayloadFromMessage(msg as Record<string, unknown>));
            } else {
                logger.error("Received post message is neither an object nor a string.", msg);
            }
        },
        [sendToWizard]
    );

    function isDebugEnabled() {
        const params = new URLSearchParams(window.location.search);
        return params.get("debug") === "true";
    }

    function hasValidRawMtl(payload: Partial<WizardPayload> | null | undefined): payload is WizardPayload {
        return typeof payload?.rawMTLString === "string" && payload.rawMTLString.trim().length > 0;
    }

    function getFirstAssignmentItem(payload: Partial<WizardPayload> | null | undefined): WizardAssignmentItem | null {
        if (!payload) {
            return null;
        }

        if (Array.isArray(payload.assignment) && payload.assignment.length > 0) {
            return payload.assignment[0];
        }

        if (!payload.bmId && !payload.stMaterialId && !payload.bmName) {
            return null;
        }

        return {
            bmId: payload.bmId ?? "",
            stMaterialId: payload.stMaterialId ?? "",
            bmName: payload.bmName ?? "",
            bmDensity: payload.bmDensity ?? { value: "", unit: "" },
            stName: payload.stName ?? "",
            stDensity: payload.stDensity ?? { value: "", unit: "" },
            stCoverage: payload.stCoverage ?? { value: "", unit: "" },
            stThickness: payload.stThickness ?? { value: "", unit: "" },
        };
    }

    function normalizeWizardPayload(payload: WizardPayload): WizardPayload {
        const firstAssignment = getFirstAssignmentItem(payload);
        if (!firstAssignment) {
            return payload;
        }

        const normalizedAssignment = Array.isArray(payload.assignment) && payload.assignment.length > 0
            ? payload.assignment
            : [firstAssignment];

        return {
            ...payload,
            assignment: normalizedAssignment,
            bmId: firstAssignment.bmId,
            stMaterialId: firstAssignment.stMaterialId,
            bmName: firstAssignment.bmName,
            bmDensity: firstAssignment.bmDensity,
            stName: firstAssignment.stName,
            stDensity: firstAssignment.stDensity,
            stCoverage: firstAssignment.stCoverage,
            stThickness: firstAssignment.stThickness,
        };
    }

    function buildRowKeyFromAssignment(assignment: WizardAssignmentItem | null, parameters?: unknown): string {
        if (!assignment) {
            return "";
        }

        const densityFromParameters = getDensityFromParameters(parameters);

        return [
            assignment.bmId ?? "",
            assignment.bmName ?? "",
            densityFromParameters,
            // `${assignment.bmDensity?.value ?? ""} ${assignment.bmDensity?.unit ?? ""}`.trim(),
            assignment.stMaterialId != null ? String(assignment.stMaterialId) : "",
            assignment.stName ?? "",
            `${assignment.stDensity?.value ?? ""} ${assignment.stDensity?.unit ?? ""}`.trim(),
            assignment.stCoverage?.value ?? "",
            `${assignment.stThickness?.value ?? ""} ${assignment.stThickness?.unit ?? ""}`.trim(),
        ].join("__");
    }

    function getAssignmentForContextIndex(payload: WizardPayload, contextIndex: number): WizardAssignmentItem | null {
        const normalizedPayload = normalizeWizardPayload(payload);
        const assignments = normalizedPayload.assignment ?? [];

        if (assignments.length === 0) {
            return null;
        }

        return assignments[contextIndex] ?? assignments[0];
    }

    function buildModelScopedPayload(payload: WizardPayload, partNumber: string, contextIndex: number): WizardPayload & { partNumber: string } {
        const normalizedPayload = normalizeWizardPayload(payload);
        const scopedAssignmentItem = getAssignmentForContextIndex(normalizedPayload, contextIndex)
            ?? getFirstAssignmentItem(normalizedPayload);

        if (!scopedAssignmentItem) {
            return Object.assign({}, normalizedPayload, {
                alternates: getAlternatesForPart(normalizedPayload, partNumber),
                partNumber,
            });
        }

        const scopedAssignment = [{
            ...scopedAssignmentItem,
            partNr: partNumber,
        }];

        return Object.assign({}, normalizedPayload, {
            assignment: scopedAssignment,
            alternates: getAlternatesForPart(normalizedPayload, partNumber),
            bmId: scopedAssignmentItem.bmId,
            stMaterialId: scopedAssignmentItem.stMaterialId,
            bmName: scopedAssignmentItem.bmName,
            bmDensity: scopedAssignmentItem.bmDensity,
            stName: scopedAssignmentItem.stName,
            stDensity: scopedAssignmentItem.stDensity,
            stCoverage: scopedAssignmentItem.stCoverage,
            stThickness: scopedAssignmentItem.stThickness,
            partNumber
        });
    }

    const loadContext = useCallback(async (forceUpdate = false) => {
        try {
            const data = await getMatParameterFromModel();
            const matListData = await getCurrentMatListFromCache();

            const contextItems = Array.isArray(data) ? data : [];

            const activeModelName = await getActiveModelName() ?? "";
            const activeMaterialName = await getCurrentMaterialName() ?? "";
            const activeDensity = await getCurrentMaterialDensity();

            const nextContextRows: AssignmentContextRow[] = contextItems
                .map((item: CreoMatParameterItem, contextIndex: number) => {
                    const isDataConsistent =
                        matListData.length > 0 &&
                        item.bmId === matListData[0].material?.bmId &&
                        (item.currentMaterialName ?? item.bmName ?? activeMaterialName) === matListData[0].material?.bmName &&
                        String(item.stMaterialId) === String(matListData[0].material?.stMaterialId) &&
                        item.stName === matListData[0].material?.stName;

                    const stDensity = isDataConsistent
                        ? `${matListData[0].material?.stDensity?.value ?? ""} ${matListData[0].material?.stDensity?.unit ?? ""}`.trim()
                        : "";

                    const rowModelName = item.modelName ?? activeModelName;

                    return {
                        modelName: rowModelName,
                        isAssembly: Boolean(item.isAssembly),
                        bmId: item.bmId ?? "",
                        bmName: item.currentMaterialName ?? item.bmName ?? activeMaterialName,
                        bmDensity: convertDensityToKgPerDm3(item.density || activeDensity),
                        stMaterialId: String(item.stMaterialId) ?? "",
                        stName: item.stName ?? "",
                        stDensity: stDensity,
                        stCoverage: item.stCoverage ?? "",
                        stThickness: `${item.stThickness?.value ?? ""} ${item.stThickness?.unit ?? ""}`.trim(),
                        altMatId: colonSeparatedToMultiline(item.altMatId),
                        altMatName: colonSeparatedToMultiline(item.altMatName),
                        altMatAbbId: colonSeparatedToMultiline(item.altMatAbbId),
                        altMatConditionLabel: colonSeparatedToMultiline(item.altMatConditionLabel),
                        altConditionId: colonSeparatedToMultiline(item.altConditionId),
                    };
                });

            const nextSignature = JSON.stringify(nextContextRows);
            if (forceUpdate || nextSignature !== contextSignatureRef.current) {
                contextSignatureRef.current = nextSignature;
                setContextRows(nextContextRows);
            }
        } catch (err) {
            logger.error("Failed to load assignment context.", err);

            const emptySignature = JSON.stringify([]);
            if (forceUpdate || emptySignature !== contextSignatureRef.current) {
                contextSignatureRef.current = emptySignature;
                setContextRows([]);
            }
        }
    }, []);

    const refreshContextIfChanged = useCallback(async () => {
        if (contextRefreshInFlightRef.current || isCoverageEditingRef.current) {
            return;
        }

        contextRefreshInFlightRef.current = true;
        try {
            await loadContext(false);
        } finally {
            contextRefreshInFlightRef.current = false;
        }
    }, [loadContext]);

    async function loadRecentlyUsed() {
        try {
            const data = await getCurrentMatListFromCache();

            if (!data || data.length === 0) {
                setRecentlyUsedRows([]);
                return;
            }

            const validEntries = data.filter((item) => hasValidRawMtl(item?.material));

            const rows: RecentlyUsedRow[] = validEntries.map((item) => {
                const firstAssignment = getFirstAssignmentItem(item.material);
                const bmNameFromParameters = getBmNameFromParameters(item.material.parameters);
                const resolvedBmName = bmNameFromParameters || firstAssignment?.bmName || "";

                return {
                    rowKey: item.key ?? buildRowKeyFromAssignment(firstAssignment, item.material.parameters),
                    bmId: firstAssignment?.bmId ?? "",
                    bmName: resolvedBmName,
                    //bmDensity: `${item.material.bmDensity?.value ?? ""} ${item.material.bmDensity?.unit ?? ""}`.trim(),
                    bmDensity: convertDensityToKgPerDm3(
                        getDensityFromParameters(item.material.parameters) ||
                        `${firstAssignment?.bmDensity?.value ?? ""} ${firstAssignment?.bmDensity?.unit ?? ""}`.trim()
                    ),
                    stMaterialId: firstAssignment?.stMaterialId != null ? String(firstAssignment.stMaterialId) : "", // Parsing to string because payload has stId as number!!!
                    stName: firstAssignment?.stName ?? "",
                    stDensity: `${firstAssignment?.stDensity?.value ?? ""} ${firstAssignment?.stDensity?.unit ?? ""}`.trim(),
                    stCoverage: firstAssignment?.stCoverage?.value ?? "",
                    stThickness: `${firstAssignment?.stThickness?.value ?? ""} ${firstAssignment?.stThickness?.unit ?? ""}`.trim(),
                };
            });
            
            setRecentlyUsedRows(rows);
        } catch (err) {
            //debugRef.current?.log(String(err));
        }
    }

    async function processWizardPayload(msg: WizardPayload) {
        try {
            if (Array.isArray(msg.alternates)) {
                msg.alternates.forEach((alt, idx) => {
                    const key = `${alt.partNr ?? ""}:${idx}`;
                    const usesDimensionValue =
                        alt.condition != null &&
                        !alt.condition.conditionLabel &&
                        typeof alt.condition.dimensionValue === "string" &&
                        alt.condition.dimensionValue.trim().length > 0;
                    conditionValueKindsRef.current.set(key, usesDimensionValue ? "dimensionValue" : "conditionLabel");
                });
            }

            const normalizedMsg = normalizeWizardPayload(msg);
            const allTargetRows = contextRows.filter(row => !!row.modelName);
            const partTargetRows = allTargetRows.filter(row => !row.isAssembly);

            if (allTargetRows.length === 0) {
                window.alert("Please select a model before assigning parameters.");
                return;
            }

            if (!hasValidRawMtl(normalizedMsg)) {
                for (const [index, row] of allTargetRows.entries()) {
                    const modelScopedPayload = buildModelScopedPayload(normalizedMsg, row.modelName, index);
                    await setSurfaceTreatmentParameters(modelScopedPayload);
                }

                loadContext(true);
                return;
            }

            if (partTargetRows.length === 0) {
                window.alert("Please select a part before assigning a material.");
                return;
            }

            for (const [index, row] of allTargetRows.entries()) {
                if (row.isAssembly) {
                    continue;
                }

                const modelScopedPayload = buildModelScopedPayload(normalizedMsg, row.modelName, index);

                const mtlSuccess = await processReceivedMaterial(modelScopedPayload);
                if (!mtlSuccess) {
                    logger.warning(`Material file assignment failed for part '${row.modelName}'.`);
                    continue;
                }
                await setAllModelParameters(modelScopedPayload);
            }

            await writeRecentMaterialToCache(normalizedMsg);
            loadContext(true);
            loadRecentlyUsed();
        } catch (err) {
            logger.error("Material assignment failed.", err);
        }
    }

    async function assignMaterialFromRecentlyUsed() {
        const hasTargetPart = contextRows.some(row => !!row.modelName && !row.isAssembly);
        if (!hasTargetPart) {
            window.alert("Please select a part before assigning a material.");
            return;
        }

        if (!selectedRow?.rowKey) {
            window.alert("No material to assign selected.");
            return;
        }

        const selectMat = await getMaterialDataFromCacheByKey(String(selectedRow?.rowKey));
        if (!selectMat) {
            window.alert("Selected material could not be loaded from cache.");
            return;
        }

        processWizardPayload(selectMat);
    }

    const handleClearRecentlyUsed = useCallback(() => {
        setRecentlyUsedRows([]);
        setSelectedRow(null);
        deleteCacheFile();
    }, []);

    const handleDeleteRecentlyUsedRow = useCallback(async (rowKey: string) => {
        setRecentlyUsedRows(prev => prev.filter(row => row.rowKey !== rowKey));
        setSelectedRow(prev => (prev?.rowKey === rowKey ? null : prev));
        await deleteCacheEntryByKey(rowKey);
    }, []);

    useEffect(() => {
        window.addEventListener("message", onMessageFromWizard);

        return () => {
            window.removeEventListener("message", onMessageFromWizard);
        };
    }, [onMessageFromWizard]);

    useEffect(() => {
        let cancelled = false;

        const initCreoScripts = async () => {
            try {
                ensureCreoCustomScripts();
                await ensureCreoRuntimeScript();
                if (!cancelled) {
                    setIsCreoRuntimeReady(true);
                }
            } catch (err) {
                if (!cancelled) {
                    logger.error("Failed to initialize CreoJS scripts.", err);
                }
            }
        };

        initCreoScripts();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isCreoRuntimeReady) {
            return;
        }

        const debugFn = async (msg: string) => {
            debugRef.current?.log(String(msg));
        };

        window.debug = debugFn;
        globalThis.debug = debugFn;

        // Load and set the assignment context table
        loadContext(true);
        loadRecentlyUsed();

        return () => {
            delete window.debug;
            delete globalThis.debug;
        };
    }, [isCreoRuntimeReady, loadContext]);

    useEffect(() => {
        if (!isCreoRuntimeReady) {
            return;
        }

        const intervalId = window.setInterval(() => {
            refreshContextIfChanged();
        }, CONTEXT_REFRESH_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [isCreoRuntimeReady, refreshContextIfChanged]);

    // if (loading) return <div>Loading ...</div>;
    // if (error) return <div>Error: {error}</div>;

    const handleOpenWizard = useCallback(() => {
        var widthW=1600;
	    var heightW=1160;
        var size="width="+widthW+",height="+heightW;
        wizardWinRef.current = window.open(

            //"https://abbprostep.totalmateria.com/prostep?skipAuth=true&leftConnector=creo",
            //"https://abbprostep.totalmateria.com/prostep?leftConnector=creo",
            //"http://localhost:3000/prostep?leftConnector=creo",
            //'https://abbprostep.totalmateria.com',
            //"/prostep?skipAuth=true&leftConnector=creo",
            "/prostep?leftConnector=creo",
            "Assignment Wizard",
            size
        );
    }, []);

    async function clearAllParameters() {
        const targetRows = contextRows.filter(row => !!row.modelName);
        if (targetRows.length === 0) {
            window.alert("Please select a model before clearing parameters.");
            return;
        }

        let clearedCount = 0;
        for (const row of targetRows) {
            const isCleared = await clearAllPluginParameters({
                partNumber: row.modelName,
            });

            if (isCleared) {
                clearedCount += 1;
            }
        }

        if (clearedCount === 0) {
            window.alert("No parameters could be cleared from the selected models.");
            return;
        }

        loadContext(true);
    }

    return (
        <div>
            <PluginHeader />
            <div className="page-container">
                <div className="plugin-page-content">
                    <div className="button-container">
                        <button className="open-button" onClick={handleOpenWizard}>
                            Open Assignment Wizard
                        </button>
                        <button className="clear-parameters-button" onClick={clearAllParameters} disabled={contextRows.length === 0}>
                            Remove Material Information
                        </button>
                    </div>

                    <div style={{ marginTop: "60px", marginBottom: "16px", fontSize: "18px", fontWeight: "600", textAlign: "center" }}>
                        Assignment Context
                    </div>
                    <AssignmentContextTable
                        rows={contextRows}
                        onCoverageChange={handleCoverageChange}
                        onCoverageEditingChange={(isEditing) => {
                            isCoverageEditingRef.current = isEditing;
                        }}
                    />

                    <div style={{ marginTop: "60px", marginBottom: "16px", fontSize: "18px", fontWeight: "600", textAlign: "center" }}>
                        Recently Used Materials
                    </div>
                    <RecentlyUsedTable rows={recentlyUsedRows} onSelectionChange={(row) => setSelectedRow(row)} onClear={handleClearRecentlyUsed} onDeleteRow={handleDeleteRecentlyUsedRow} />
                </div>
                
                <div className="assign-button-container">
                    <button className="assign-button" onClick={assignMaterialFromRecentlyUsed}>
                        Assign Material
                    </button>
                </div>
            </div>

            {showDebug && <DebugConsole ref={debugRef} />}
        </div>
    );
}